import { Component, Event, EventEmitter, Listen, Prop, State, h, Host, Method } from '@stencil/core';
import { default as languageStrings } from '../../global/l10n/UploadMytiLists.json';
import * as XLSX from 'xlsx';
import { SheetInfo, UploadInfo } from './upload-myti-list.interface';
import * as ListManagerInterfaces from '../add-to-myti-list/add-to-myti-list.interface';
import { fetchWithTimeout } from '@ticom/web-component-utils';

@Component({
    tag: 'ti-upload-myti-list',
    styleUrl: './upload-myti-list.scss',
    shadow: true
})
export class UploadMytiList {

    /**
     * App settings value to have settings based on user
     */
    private _appSettings: ListManagerInterfaces.AppSettings = null;

    /**
     * Data for the current listID
     */
    private _currentList: ListManagerInterfaces.ListInfo = null;

    /**
     * How many rows to display in the table
     */
    private _displayRowLimit: number = 5;

    /**
    * Max items allowed in list based on user
    */
    private _itemsLimit: number = null;

    /**
     * Boolean variable to track data validity for Add to List button unable/disable
     */
    private _isValidData: boolean = false;

    /**
     * Locale-specific set of language strings
     */
    private _localeStrings: Record<string, string>;

    /**
    * Uploaded data is restricted to show _maxNumRows records in UI.
    */
    private _maxNumRows: number = null;

    /**
     * Number of rows in uploaded file
     */
    private _numRowsInFile: number = 0;

    /**
     * Final number of parts to be added if limit is crossed, to be shown in warning
     */
    private _partAdded: number = 0;

    /**
     * Reading excel data
     */
    private _readExcelData: any = null;

    /**
     * Start rows drop down options
     */
    private _startRows: string = '1,2,3,4,5,6,7,8,9,10';

    /**
     * Element ref for the table
     */
    private _tableElement: HTMLTableElement;

    /**
     * Template download URL
     */
    private _templateUrl: string = '';

    /**
    * Input file
    */
    private _uploadFile: File = null;

    /**
     * To store the current user info
     */
    private _userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Final validated data to be sent
     */
    private _validatedData: ListManagerInterfaces.ListItem[] = [];

    /**
     * Reference for warning message
     */
    private _warningRef: HTMLDivElement;

    /**
     * Reference for waning message text
     */
    private _warningTextRef: HTMLSpanElement;

    /**
     * Specifies progress state while data is loaded
     */
    @State() progressState: 'init' | 'loading' | 'uploading' | 'error' | 'ready' = 'loading';

    /**
     * Default start at row
     */
    @State() startAtRow: number = 2;

    /**
     * Specifies uploaded file information
     */
    @State() uploadInfo: UploadInfo;

    /**
    * Current data for the list being updated
    */
    @Prop() currentList: ListManagerInterfaces.ListInfo;

    /**
     * Passed from parent component to uniquely identify events from this instance
     */
    @Prop() contextId: string = null;

    /**
    * INIT url for api calls
    */
    @Prop() domain: string;

    /**
    * Maximum number of parts allowed in uploaded list
    */
    @Prop() itemsLimit: number = null;

    /**
     * Specifies the locale
     */
    @Prop() locale: string = 'en-US';

    /**
     * List Id for current list
     * should be passed from details page/grid
     */
    @Prop() listId: number = null;

    /**
     * User details
     * should be passed from details page/grid
     */
    @Prop() userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Event to be emitted in case of any error
     */
    @Event() myTiUploadListErrorEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerErrorEventDetail>;

    /**
     * Event emitted on item upload to the list with data validity flag
     */
    @Event() myTiBulkAddtoListEvent: EventEmitter<ListManagerInterfaces.MyTiBulkAddtoListEventDetail>;

    async componentWillLoad() {
        // load language strings for locale - fallback to 'en-US' or an empty object
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};
        await this._initData();
    }

    componentDidRender() {
        if (this.progressState === 'ready') {
            //Once uploaded file content is rendered validate the data and emit event in _proceed() method
            this._proceed();
        }
    }

    /**
     * Method to listen to file upload
     */
    @Listen('tiFileUploaded', { target: 'body' })
    async onTiFileUploaded(ev: CustomEvent<File>) {
        this.progressState = 'uploading';

        // Stop propagation so that other instances are not affected
        ev.stopPropagation();
        ev.preventDefault();

        this._uploadFile = ev.detail;

        await this._readFile();

        // maxrows for filtering excel rows based on limit, adding 10 as a buffer in case user wants to start from 10th row
        this._maxNumRows = this._itemsLimit + 10;
    }

    /**
     * Method to get final uploaded data
     */
    @Method()
    async getItemsToAdd(): Promise<ListManagerInterfaces.ListItem[]> {
        return this._validatedData;
    }

    /**
     * Method to clear out uploaded data
     */
    private _clearData() {
        this.uploadInfo = {} as UploadInfo;
        this.uploadInfo.sheetData = new Map<string, SheetInfo>();
        this._numRowsInFile = 0;
        this._isValidData = false;
        this.startAtRow = 2;
    }

    /**
     * Make api call to get the app settings
     */
    private async _getAppSettings(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/settings'
            : 'data/add-to-myti-list/app-settings.json';

        let errorResponseStatus: number = null;

        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting App Settings');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.AppSettings) => {
                this._appSettings = data;
            });
        } catch (err) {
            this.myTiUploadListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
        }

        if (!this._itemsLimit) {
            this._itemsLimit = this._appSettings ? this._getListItemsLimit() : ListManagerInterfaces.DEFAULT_LIST_ITEMS_LIMIT;
        }
    }

    /**
    * Method to return content for upload
    */
    private _getBaseContent() {
        return [
            <p>{this._localeStrings['uploadToListLimitText'].replace('{0}', this._currentList.totalItemCount.toString()).replace('{1}', this._itemsLimit.toString())}</p>,
            <ti-drag-and-drop browse-label="Choose file" drag-and-drop-label="Drop a file to upload">
                <h4>{this._localeStrings['uploadFileText']}</h4>
                <p><b>{this._localeStrings['chooseOrDragText']}</b></p>
                <p>{this._localeStrings['uploadLimitationsText'].replace('{0}', Math.min(this._itemsLimit, ListManagerInterfaces.DEFAULT_ITEM_EDIT_LIMIT).toString())}</p>

                <ti-file-download file-name="list-manager-upload-template.xlsx" file-src={this._templateUrl}>
                    <ti-button appearance="text">
                        <ti-svg-icon appearance="secondary" icon-set="actions" size="s">download</ti-svg-icon>
                        {this._localeStrings['downloadTemplateText']}
                    </ti-button>
                </ti-file-download>
            </ti-drag-and-drop>
        ];
    }

    /**
     * Method to extract unique values based on current list content
     */
    private _getCellValues() {
        if (this._tableElement) {
            // Currently rows are hidden with display: none, and we need table rows to validate data
            // Ideally we want a golden source for data before we display the table and only include visible rows in the table
            const rows = this._tableElement.tBodies[0].rows;
            const sheet = this.uploadInfo.sheetData.get(this.uploadInfo.selectedSheet);
            sheet.cells = [];

            Array.from(rows).forEach((row) => {
                const cells = row.cells;
                const partNumber = cells[0].textContent;
                sheet.cells.push([partNumber, cells[1].textContent]);
            });
        }
    }

    /**
     * Method to return content of file
     */
    private _getCoreContent() {
        const sheet = this.uploadInfo.sheetData.get(this.uploadInfo.selectedSheet);
        const headers = sheet.headers;
        const colIndex = sheet.colIndex;
        // Dropdown options for selecting column
        const colHeaderOptions = sheet.headers.join();

        return [
            <p>
                <ti-button
                    appearance="link"
                    data-lid='document_center_modal_editfile'
                    onClick={() => this._returnToUpload()}
                    data-navtitle='Return to file upload'>
                    <ti-svg-icon appearance='secondary' size="s">arrow-left</ti-svg-icon>
                    {this._localeStrings['returnToUploadText']}
                </ti-button>
            </p>,
            <p><b>{this._localeStrings['fileUploadedText']}</b></p>,
            <p>
                <div class="ti-upload-myti-list-filename">
                    <ti-svg-icon size="m" appearance="secondary">document-generic</ti-svg-icon>
                    <span data-di-mask class="ti-inline-filename">
                        {this.uploadInfo.filename}
                    </span>
                </div>
            </p>,
            <p>
                <div ref={el => this._warningRef = el} role="alert">
                    <div class="ti-upload-myti-list-warning">
                        <ti-svg-icon class="ti-page-alert-icon" appearance='warn' size="m">{'warning'}</ti-svg-icon>
                        <span ref={el => this._warningTextRef = el}>{this._localeStrings['fileWarningText'].replace('{0}', Math.min(this._itemsLimit, ListManagerInterfaces.DEFAULT_ITEM_EDIT_LIMIT).toString()).replace('{1}', this._partAdded.toString())}</span>
                    </div>
                </div>
            </p>,
            <div class="ti-upload-myti-list-container">
                <div class="ti-upload-myti-list-forms">
                    <div class="ti-upload-myti-list-form-item">
                        <label class="ti-upload-myti-list-label">{this._localeStrings['selectSheetLabel']}</label>
                        <ti-select class="ti-upload-myti-list-select"
                            data-lid="Select sheet"
                            options={this.uploadInfo.sheetNames}
                            value={this.uploadInfo.defaultSheet}
                            onTiChange={e => this._updateSheet(e.target['value'])}
                        ></ti-select>
                    </div>
                    <div class="ti-upload-myti-list-form-item">
                        <label class="ti-upload-myti-list-label">{this._localeStrings['startAtRow']}</label>
                        <ti-select class="ti-upload-myti-list-select"
                            data-lid="Select start row"
                            options={this._startRows}
                            value={this.startAtRow.toString()}
                            onTiChange={e => this._updateStartRow(Number(e.target['value']))}
                        ></ti-select>
                    </div>
                </div>
                <div class="ti-upload-myti-list-table">
                    <table class="ti-upload-myti-list-worksheet-table" ref={el => this._tableElement = el}>
                        <colgroup>
                            <col style={{ width: '50%' }} />
                            <col style={{ width: '50%' }} />
                        </colgroup>
                        <thead>
                            <tr class="ti-upload-myti-list-table-header1">
                                <th>
                                    <span class="ti-upload-myti-list-col-header">{this._localeStrings['partNumberLabel']}</span>
                                </th>
                                <th class="ti-upload-myti-list-hidden">
                                    <span class="ti-upload-myti-list-col-header">{this._localeStrings['customerRefLabel']}</span>
                                    <ti-tooltip-trigger>
                                        <ti-svg-icon size="s">info-circle</ti-svg-icon>
                                        <ti-tooltip position="auto">
                                            <span>{this._localeStrings['customerRefToolTip']}</span>
                                        </ti-tooltip>
                                    </ti-tooltip-trigger>
                                </th>
                            </tr>
                            <tr class="ti-upload-myti-list-table-header2">
                                <th>
                                    <p class="ti-upload-myti-list-select-label">{this._localeStrings['useColumnHint']}</p>
                                    <ti-select data-lid="Use column" options={colHeaderOptions} value={headers[colIndex[0]]}
                                        onTiChange={e => this._updateColumn(e.target['value'], 0)}
                                    ></ti-select>
                                </th>
                                <th class="ti-upload-myti-list-hidden">
                                    <p class="ti-upload-myti-list-select-label">{this._localeStrings['useColumnHint']}</p>
                                    <ti-select data-lid="Use column" options={colHeaderOptions} value={headers[colIndex[1]] ?? null}
                                        onTiChange={e => this._updateColumn(e.target['value'], 1)}
                                    ></ti-select>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {this._getRows()}
                        </tbody>
                    </table>
                </div>
            </div>
        ];
    }

    /**
     * Make api call to get the current List
     */
    private async _getCurrentListInfo(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + `/listmodel/api/v1/ext/secured/list/${this.listId}`
            : 'data/add-to-myti-list/edit-list.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error(`Error in getting List with id :: ${this.listId}`);
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo) => {
                this._currentList = data;
            });
        } catch (err) {
            this.myTiUploadListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
            this.progressState = 'error';
        }
    }

    /**
    * Return error content
    */
    private _getErrorContent() {
        return [
            <div class="ti-upload-mylist-dialog-container">
                <p>
                    <ti-button
                        appearance="link"
                        data-lid='document_center_modal_editfile'
                        onClick={() => this._returnToUpload()}
                        data-navtitle='Return to file upload'
                    >
                        <ti-svg-icon appearance='secondary' size="s" class="u-margin-right-2">arrow-left</ti-svg-icon>
                        {this._localeStrings['returnToUploadText']}
                    </ti-button>
                </p>
                <p>{this._localeStrings['uploadToListErrorText']}</p>
            </div>
        ];
    }

    /**
     * Return the list limit from App Settings based on userType
     */
    private _getListItemsLimit(): number {
        if (this._userInfo.isCGX) {
            return this._appSettings.maxItemPerListCGX;
        } else {
            switch (this._userInfo.userType) {
                case 'TIER': return this._appSettings.maxItemPerListTIer;
                case 'DIRECT': return this._appSettings.maxItemPerListDirect;
                case 'MASSMARKET': return this._appSettings.maxItemPerListMassMarket;
                default: return ListManagerInterfaces.DEFAULT_LIST_ITEMS_LIMIT;
            }
        }
    }

    /**
     * Return the loading content
     */
    private _getLoadingContent() {
        return [
            <div class="ti-upload-mylist-dialog-container">
                {this.progressState === 'uploading' && <p>{this._localeStrings['fileUploadingText']}</p>}
                <ti-progress></ti-progress>
            </div>
        ];
    }

    /**
     * Method to return row content for table
     */
    private _getRows() {
        const sheet = this.uploadInfo.sheetData.get(this.uploadInfo.selectedSheet);
        const colIndex = sheet.colIndex;
        const rows = [];
        const numRows = sheet.data.length;
        const endRowIndex = this.startAtRow + numRows - 1;

        let count = 0;
        sheet.data.map((row, index) => {
            if (index >= this.startAtRow - 1 && index <= endRowIndex) {
                const rowData = Object.values(row);
                count++;
                // Hide rows after 5 to display
                rows.push(
                    <tr style={{ display: count > this._displayRowLimit ? 'none' : '' }}>
                        <td>{sheet.emptySheet ? null : rowData[colIndex[0]]}</td>
                        <td>{(sheet.emptySheet || sheet.emptyCpnColumn) ? null : rowData[colIndex[1]]}</td>
                    </tr>
                );
            }
        });
        return rows;
    }

    /**
     * Make api call to get user info
     */
    private async _getUserInfo() {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/user'
            : 'data/add-to-myti-list/user-info.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Info');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.UserInfo) => {
                this._userInfo = data;
            });
        } catch (err) {
            this.myTiUploadListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err });
            this.progressState = 'error';
        }
    }

    /**
     * Method to initialize data for props
     */
    private async _initData() {
        this.progressState = 'loading';

        // initialize the props
        if(!this.userInfo) {
            await this._getUserInfo();
        } else {
            this._userInfo = this.userInfo;
        }

        // initialize file variables
        this.uploadInfo = {} as UploadInfo;
        this.uploadInfo.sheetData = new Map<string, SheetInfo>();

        let promise1: Promise<void>;
        let promise2: Promise<void>;

        // Set template URL
        if (this.domain) {
            this._templateUrl = this.domain + '/list-manager/downloads/template';
        }

        // If current list is not provided check for listId and fetch details
        if (!this.currentList && null != this.listId) {
            promise1 = this._getCurrentListInfo();

        } else if (null === this.currentList && null === this.listId) {
            this.progressState = 'error';
        }
        else {
            this._currentList = this.currentList;
        }

        // Set item limit
        if (!this.itemsLimit) {
            promise2 = this._getAppSettings();
        } else {
            this._itemsLimit = this.itemsLimit;
        }

        await Promise.all([promise1, promise2]);
        this.progressState = 'init';
    }

    /**
     * Generic method to make all api calls
     */
    private async _makeApiCall(apiUrl: string,
        methodType: ListManagerInterfaces.HttpMethodType,
        requestBody: any,
        timeout: number = ListManagerInterfaces.LIST_API_TIMEOUT_VALUE
    ): Promise<Response> {
        const apiResponse: Response = await fetchWithTimeout(apiUrl,
            {
                method: methodType,
                body: requestBody !== null && requestBody !== undefined ? JSON.stringify(requestBody) : null,
                credentials: 'include',
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                }
            }, timeout);

        return apiResponse;
    }

    /**
     * Method to return validated data on successful upload and parsing
     */
    private _proceed() {
        this._validatedData = this._validate();
        if (this._validatedData !== null && !this._isValidData) {
            // Set valid flag as true and emit the event
            this._isValidData = true;

            this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, isValid: this._isValidData, action: 'upload' });
        } else if (!this._validatedData) {
            if (this._isValidData) {
                // disable valid check and button
                this._isValidData = false;
                this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, isValid: this._isValidData, action: 'upload' });
            }

            // show warning
            this._warningRef.style.display = 'block';
            this._warningTextRef.innerHTML = this._localeStrings['error-empty-list'];
        }
    }

    /**
     * Method to read excel file uploaded
     */
    private async _readExcel(inFile: File) {
        const reader: FileReader = new FileReader();
        this._readExcelData = (maxRows) => {
            return new Promise(resolve => {
                reader.onload = function (event) {
                    try {
                        const sheetArray = [];
                        const data = new Uint8Array(event.target.result as ArrayBuffer);
                        const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'array', sheetRows: maxRows });
                        workbook.SheetNames.forEach(function (sheetName) {
                            const sheetInfo = {} as SheetInfo;
                            sheetInfo.sheetName = sheetName;
                            sheetInfo.qty = -1;
                            const worksheet: XLSX.WorkSheet = workbook.Sheets[sheetName];
                            sheetInfo.data = XLSX.utils.sheet_to_json(worksheet, { raw: true, header: 1, blankrows: false });

                            // Filter out empty row and cells
                            // Check case sensitivity for part Numbers in excel
                            sheetInfo.data = sheetInfo.data.filter(row => row.join('') !== '')
                                .map((row, index) => {
                                // Filter out empty cells
                                    const filteredRow = row.filter((cel) => cel);
                                    if (index === 0) {
                                        return filteredRow;
                                    }
                                    // For other rows, trim and uppercase the first cell, and return others unchanged
                                    return filteredRow.map((cel, pos) => {
                                        return pos === 0 && cel ? cel.trim()?.toUpperCase() : cel;
                                    });
                                });

                            sheetArray.push(sheetInfo);
                        });
                        resolve(sheetArray);
                    }
                    catch (e) {
                        console.error('Error occurred while reading file - ', e);
                    }
                };

            }).catch(() => {
                this.progressState = 'error';
                console.error('Error occurred while reading file');
            });
        };

        reader.readAsArrayBuffer(inFile);
        this._readRows();
    }

    /**
     * Method to read file and update metadata for uploadInfo from file
     */
    private async _readFile() {
        if (this._uploadFile) {
            this.uploadInfo.filename = this._uploadFile.name;
            this.uploadInfo.filesize = this._uploadFile.size;
            this.uploadInfo.filetype = this._uploadFile.type;
            this.uploadInfo.status = 'loading';
            if (
                this._uploadFile.name.toLowerCase().endsWith('.xls') ||
                this._uploadFile.name.toLowerCase().endsWith('.xlsx')
            ) {
                await this._readExcel(this._uploadFile);
            }
        }
    }

    /**
     * Method to prepare sheet info and data from excel data
     */
    private _readRows() {
        if (this._readExcelData) {
            this._readExcelData(this._maxNumRows).then(async (data: any[]) => {
                const sheetData = data;
                sheetData.forEach(info => {
                    const sheetInfo = info as SheetInfo;
                    if (sheetInfo.data && sheetInfo.data.length > 0) {
                        if (!this.uploadInfo.selectedSheet) {
                            this.uploadInfo.selectedSheet = sheetInfo.sheetName;
                        }
                        if (!this.uploadInfo.defaultSheet) {
                            this.uploadInfo.defaultSheet = sheetInfo.sheetName;
                        }
                        sheetInfo.headers = [];
                        sheetInfo.colIndex = [];
                        sheetInfo.data[0].forEach((value, index) => {
                            if (value) {
                                sheetInfo.headers.push(value);
                                sheetInfo.colIndex.push(index);
                            }
                        });

                        if (sheetInfo.headers.length === 0) {
                            sheetInfo.headers.push(this._localeStrings['useColumnNotFound']);
                            sheetInfo.emptySheet = true;
                        } else {
                            sheetInfo.emptySheet = false;
                        }

                        // Remove duplicate and null entries from sheet
                        const uniqueParts: Set<string> = new Set();
                        sheetInfo.data = sheetInfo.data.filter(row => {
                            const partNumber = row.values().next()?.value;
                            if (partNumber && partNumber.trim().length > 0 && !uniqueParts.has(partNumber)) {
                                uniqueParts.add(partNumber);
                                return true;
                            }
                            return false;
                        });

                        this._numRowsInFile = sheetInfo.data.length;
                        if (sheetInfo.data.length > this._maxNumRows) {
                            sheetInfo.data = sheetInfo.data.slice(0, this._maxNumRows);
                        }

                        this.uploadInfo.sheetData.set(sheetInfo.sheetName, sheetInfo);
                        if (!this.uploadInfo.sheetNames) {
                            this.uploadInfo.sheetNames = sheetInfo.sheetName;
                        } else {
                            this.uploadInfo.sheetNames += ',' + sheetInfo.sheetName;
                        }
                    }
                });
                this.uploadInfo.status = 'OK';
                setTimeout(() => {
                    this.progressState = 'ready';
                }, 500);
            }).catch((err: Error) => {
                console.error('Error occurred in reading file -', err);
                this.progressState = 'error';
            });
        }

    }

    /**
     * Method to return back to upload file
     */
    private _returnToUpload() {
        this._clearData();
        this.progressState = 'init';
    }

    /**
     * Method to update selected sheet from dropdown
     */
    private async _updateSheet(value) {
        this._numRowsInFile = this.uploadInfo.sheetData.get(value).data.length;
        this.startAtRow = 2;
        this.uploadInfo = { ...this.uploadInfo, selectedSheet: value };
    }

    /**
     * Method to update selected startRow value from dropdown
     */
    private _updateStartRow(startRow: number) {
        this.startAtRow = startRow;
        this._numRowsInFile = this.uploadInfo.sheetData.get(this.uploadInfo.selectedSheet).data.length;
        if (startRow > 0 && startRow <= this._numRowsInFile) {
            // hide warning
            this._warningRef.style.display = 'none';

            this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, isValid: this._isValidData, action: 'upload' });
        } else {
            // Rows don't exist in sheet, so show warning
            this._warningRef.style.display = 'block';
            this._warningTextRef.innerHTML = this._localeStrings['error-empty-list'];

            this.myTiBulkAddtoListEvent.emit({ contextId: this.contextId, isValid: false, action: 'upload' });
        }
    }

    /**
     * Method to change column name to be used from file
     */
    private async _updateColumn(colName: string, index: number) {
        const sheet = this.uploadInfo.sheetData.get(this.uploadInfo.selectedSheet);
        const colIndex = sheet.colIndex;
        const currentColIndex = colIndex[index];
        const newColIndex = sheet.headers.findIndex((element) => { return (element === colName); });
        if (newColIndex != -1 && (currentColIndex !== newColIndex || index === 1)) {
            sheet.colIndex[index] = newColIndex;
            this.uploadInfo.sheetData.set(this.uploadInfo.selectedSheet, sheet);
            this.uploadInfo = { ...this.uploadInfo };
        }
    }

    /**
     * Method to validate data based on existing list details and user settings and limit
     */
    private _validate() {
        const sheet = this.uploadInfo.sheetData.get(this.uploadInfo.selectedSheet);
        // Update and validate cell values from table
        this._getCellValues();
        if (sheet.cells.length === 0) {
            this._warningRef.style.display = 'block';
            this._warningTextRef.innerHTML = this._localeStrings['error-empty-list'];
            return null;
        }
        const partsLimit = Math.min(this._itemsLimit - this._currentList.totalItemCount, ListManagerInterfaces.DEFAULT_ITEM_EDIT_LIMIT);
        // Check for item limit and show warning accordingly
        if (sheet.cells.length > partsLimit) {
            sheet.cells = sheet.cells.slice(0, partsLimit);
            this._partAdded = sheet.cells.length;
            this._warningRef.style.display = 'block';
            this._warningTextRef.innerHTML = this._localeStrings['fileWarningText'].replace('{0}', Math.min(this._itemsLimit, ListManagerInterfaces.DEFAULT_ITEM_EDIT_LIMIT).toString()).replace('{1}', this._partAdded.toString());
        } else {
            this._warningRef.style.display = 'none';
        }

        const validatedData: ListManagerInterfaces.ListItem[] = sheet.cells.map((nested) => {
            const listItem: ListManagerInterfaces.ListItem = {
                itemId: null,
                listId: this.listId,
                partId: null,
                partNumber: nested[0],
                type: null,
                customerReferenceNumber: nested[1],
                quantity: 1,
                itemInfo: null
            };
            return listItem;
        });
        return validatedData;
    }

    render() {
        switch (this.progressState) {
            case 'init': return <Host>{this._getBaseContent()}</Host>;
            case 'loading':
            case 'uploading': return <Host>{this._getLoadingContent()}</Host>;
            case 'ready': return <Host>{this._getCoreContent()}</Host>;
            case 'error': return <Host>{this._getErrorContent()}</Host>;
        }
    }
}
